import React, { useState, useEffect } from "react";
import axios from "axios";
import { message, Spin, Typography, Tag } from "antd";
import { SearchOutlined, AimOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { ModalItem, InputItem, ButtonItem } from "../../../../../Components/Items";
import TextAreaItem from "../../../../../Components/TextAreaItem";
import { WA } from "../../theme";
import { parseGoogleMapsUrl } from "../Helpers/ubicacionesHelpers";
import BarraFormato from "../BarraFormato";

const { Text } = Typography;

const ComposerUbicacion = ({ visible, modo, headersToken, empresaId, onCancel, onEnviar }) => {
  const esPedir = modo === "location_request";

  const [texto, setTexto] = useState("Por favor comparte tu ubicación.");
  const [query, setQuery] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [sel, setSel] = useState(null); // { lat, lng, name, address }
  const [guardadas, setGuardadas] = useState([]);
  const [linkMaps, setLinkMaps] = useState("");

  useEffect(() => {
    if (!visible || esPedir) return;
    axios
      .post("whatsapp/ubicacionListar", { empresaId }, { headers: headersToken })
      .then((r) => {
        if (r.data.success === 2) setGuardadas(r.data.ubicaciones || []);
      })
      .catch(() => {});
  }, [visible, esPedir, empresaId, headersToken]);

  const elegirGuardada = (u) => {
    setSel({
      lat: u.latitude,
      lng: u.longitude,
      name: u.nombre,
      address: u.descripcion || u.direccion || "",
    });
    setResultados([]);
  };

  // Al pegar un link de Google Maps, extrae las coordenadas del pin.
  const cambiarLink = (valor) => {
    setLinkMaps(valor);
    if (!valor.trim()) return;
    const coords = parseGoogleMapsUrl(valor);
    if (coords) {
      setSel((prev) => ({
        lat: coords.lat,
        lng: coords.lng,
        name: prev?.name || "",
        address: prev?.address || "",
      }));
      setResultados([]);
      message.success("Coordenadas detectadas del link.");
    } else {
      message.warning(
        "No se detectaron coordenadas. Usa el URL largo de Google Maps (el de la barra del navegador), no el link corto de Compartir.",
      );
    }
  };

  const limpiar = () => {
    setTexto("Por favor comparte tu ubicación.");
    setQuery("");
    setResultados([]);
    setBuscando(false);
    setSel(null);
    setLinkMaps("");
  };

  const cerrar = () => {
    limpiar();
    onCancel();
  };

  const buscar = async () => {
    if (!query.trim()) return;
    setBuscando(true);
    try {
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=6&q=${encodeURIComponent(query)}`,
      );
      setResultados(await resp.json());
    } catch {
      message.error("No se pudo buscar la dirección.");
    } finally {
      setBuscando(false);
    }
  };

  const elegir = (r) => {
    setSel({
      lat: parseFloat(r.lat),
      lng: parseFloat(r.lon),
      name: (r.display_name || "").split(",")[0] || "",
      address: r.display_name || "",
    });
    setResultados([]);
  };

  const usarActual = () => {
    if (!navigator.geolocation) return message.error("Tu navegador no soporta geolocalización.");
    setBuscando(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        let address = "";
        let name = "Mi ubicación";
        try {
          const resp = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
          );
          const d = await resp.json();
          address = d.display_name || "";
          name = (d.display_name || "").split(",")[0] || name;
        } catch {}
        setSel({ lat, lng, name, address });
        setBuscando(false);
      },
      () => {
        setBuscando(false);
        message.error("No se pudo obtener tu ubicación.");
      },
    );
  };

  const enviar = () => {
    if (esPedir) {
      onEnviar({ tipo: "location_request", contenido: { texto } });
      limpiar();
      return;
    }
    if (!sel) return message.error("Busca o elige una ubicación.");
    onEnviar({
      tipo: "location",
      contenido: { latitude: sel.lat, longitude: sel.lng, name: sel.name, address: sel.address },
    });
    limpiar();
  };

  const bbox = sel
    ? `${sel.lng - 0.005},${sel.lat - 0.005},${sel.lng + 0.005},${sel.lat + 0.005}`
    : "";

  return (
    <ModalItem
      title={esPedir ? "Pedir ubicación" : "Enviar ubicación"}
      visible={visible}
      onCancel={cerrar}
      width={520}
      zIndex={1100}
      destroyOnClose
      spinning={false}
    >
      {esPedir ? (
        <>
          <BarraFormato targetId="wa-cu-texto" value={texto} onChange={setTexto} />
          <TextAreaItem
            id="wa-cu-texto"
            placeholder="Mensaje para pedir la ubicación"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            rows={2}
          />
        </>
      ) : (
        <>
          {guardadas.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <Text style={{ fontSize: 12, color: WA.textSecondary }}>Ubicaciones guardadas</Text>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                {guardadas.map((u) => (
                  <Tag
                    key={u._id}
                    onClick={() => elegirGuardada(u)}
                    icon={<EnvironmentOutlined />}
                    color={WA.turquesa}
                    style={{ cursor: "pointer", padding: "3px 8px", borderRadius: 14 }}
                  >
                    {u.nombre}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          <InputItem
            placeholder="Pega aquí un link de Google Maps"
            value={linkMaps}
            onChange={(e) => cambiarLink(e.target.value)}
          />
          <div style={{ fontSize: 11, color: WA.textSecondary, margin: "4px 0 8px" }}>
            Copia el link de la <b>barra del navegador</b> (el que tiene <code>/@...</code> en la
            dirección). El botón <b>"Compartir"</b> da un link corto que <b>no</b> incluye las
            coordenadas.
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <InputItem
              placeholder="Buscar dirección o lugar..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onPressEnter={buscar}
            />
            <ButtonItem cyan icon={<SearchOutlined />} onClick={buscar} />
          </div>

          <div style={{ fontSize: 11, color: WA.textSecondary, marginTop: 4 }}>
            El buscador puede ser <b>impreciso</b>. Si la dirección es importante, mejor usa el link
            de Google Maps y verifica el pin abajo antes de enviar.
          </div>

          <ButtonItem
            turquesa
            icon={<AimOutlined />}
            onClick={usarActual}
            style={{ marginTop: 8 }}
          >
            Usar mi ubicación actual
          </ButtonItem>
          <div style={{ fontSize: 11, color: WA.textSecondary, marginTop: 4 }}>
            Tu ubicación actual depende del GPS del dispositivo y también puede ser inexacta.
          </div>

          {buscando && (
            <div style={{ textAlign: "center", padding: 10 }}>
              <Spin size="small" />
            </div>
          )}

          {resultados.length > 0 && (
            <div
              style={{
                marginTop: 8,
                maxHeight: 160,
                overflowY: "auto",
                border: `1px solid ${WA.border}`,
                borderRadius: 8,
              }}
            >
              {resultados.map((r) => (
                <div
                  key={r.place_id}
                  onClick={() => elegir(r)}
                  style={{ padding: "8px 10px", cursor: "pointer", borderBottom: `1px solid ${WA.border}` }}
                >
                  <EnvironmentOutlined style={{ color: WA.naranja, marginRight: 6 }} />
                  <Text style={{ fontSize: 13 }}>{r.display_name}</Text>
                </div>
              ))}
            </div>
          )}

          {sel && (
            <div style={{ marginTop: 12 }}>
              <iframe
                title="mapa-preview"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${sel.lat},${sel.lng}`}
                style={{ width: "100%", height: 180, border: "none", borderRadius: 8 }}
              />
              <div style={{ marginTop: 8 }}>
                <InputItem
                  placeholder="Nombre de la ubicación"
                  value={sel.name}
                  onChange={(e) => setSel((prev) => ({ ...prev, name: e.target.value }))}
                  maxLength={60}
                />
              </div>
              <div style={{ marginTop: 8 }}>
                <InputItem
                  placeholder="Descripción / dirección (opcional)"
                  value={sel.address}
                  onChange={(e) => setSel((prev) => ({ ...prev, address: e.target.value }))}
                />
              </div>
            </div>
          )}
        </>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <ButtonItem rojo onClick={cerrar}>Cancelar</ButtonItem>
        <ButtonItem cyan onClick={enviar}>Enviar</ButtonItem>
      </div>
    </ModalItem>
  );
};

export default ComposerUbicacion;
